import { TestType } from '@risksmart-app/domain/src/types/consts/test-type';
import { z } from 'zod';

import type { UserOption } from '../../../../schemas/global';
import {
  CustomAttributeDataSchema,
  FileOrRelationSchema,
  StringDateSchema,
  UserOptionSchema,
} from '../../../../schemas/global';
// COI-991: no ceiling — the effectiveness scale is org-configurable taxonomy,
// so a fixed ceiling rejected options an org had legitimately configured. This
// form is shared by all three control-test forms (standard, internal-audit and
// oversight), and while the `.max(4)` stood it rejected a 6th option client-side
// with the message "Required", the same confusing symptom as CUS-262.
//
// The floor stays: the scale is 0-based, so no configured option is negative,
// and all three target tables carry a `>= 0` CHECK.
//
// Deliberately NOT `unboundedEffectivenessRatingNullishSchema`: that schema
// carries `.int()` and no custom message, and tightening the form to integers
// is a UX change rather than part of this fix (the COI-650 stance on web forms).
// The server enforces both the integer and the int4 bound.
const effectiveness = z
  .number({
    invalid_type_error: 'Required',
  })
  .min(0);

export const TestResultFormSchema = z
  .object({
    Title: z.string().nullish(),
    TestType: z
      .nativeEnum(TestType, {
        required_error: 'Required',
        invalid_type_error: 'Required',
      })
      .nullish(),
    Description: z.string().nullish(),
    DesignEffectiveness: effectiveness.nullish(),
    PerformanceEffectiveness: effectiveness.nullish(),
    OverallEffectiveness: effectiveness.nullish(),
    Id: z.string().uuid().optional(),
    OversightAssessmentId: z.string().uuid().nullish(),
    ParentControlIds: z
      .array(
        z.object({
          value: z
            .string({ required_error: 'Required' })
            .uuid({ message: 'Required' }),
        })
      )
      .min(1),
    Submitter: UserOptionSchema,
    TestDate: StringDateSchema,
    files: z.array(FileOrRelationSchema),
  })
  .and(CustomAttributeDataSchema);

export type TestResultFormFieldsData = z.infer<typeof TestResultFormSchema>;

/**
 * Creation is the only mode without an Id in the form values: every edit
 * flow (standard modal, oversight, internal audit, assessment finding)
 * spreads the fetched test result — including its Id — into `values`,
 * while create flows either omit Id or pass no `values` at all.
 */
export const isCreateTestResult = (
  values: Pick<TestResultFormFieldsData, 'Id'> | undefined
): boolean => !values?.Id;

export const defaultValues: TestResultFormFieldsData = {
  OverallEffectiveness: undefined as unknown as number,
  ParentControlIds: [],
  TestDate: '',
  TestType: undefined as unknown as TestType,
  Title: null,
  Description: '',
  DesignEffectiveness: null,
  PerformanceEffectiveness: null,
  files: [],
  Submitter: null as unknown as UserOption,
  CustomAttributeData: null,
  OversightAssessmentId: null,
};
